import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
  type AuthError
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { useToast } from './useToast';

const user = ref<User | null>(null);
const loading = ref(true);
const admin = ref(false);
const unsubscribe = ref<(() => void) | null>(null);
let subscribers = 0;

const isAuthenticated = computed(() => !!user.value);
const isAdmin = computed(() => admin.value);

export function useAuth() {
  const { error, success } = useToast();

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      user.value = result.user;
      await checkAdminStatus(result.user);

      if (!admin.value) {
        await signOut(auth);
        user.value = null;
        error('Tu cuenta no tiene permisos de administración', 'Acceso denegado');
        return false;
      }

      success('Inicio de sesión exitoso', 'Bienvenido');
      return true;
    } catch (err) {
      const authError = err as AuthError;
      let message = 'Credenciales inválidas o acceso no permitido';

      switch (authError.code) {
        case 'auth/too-many-requests':
          message = 'Demasiados intentos fallidos. Intenta más tarde';
          break;
        case 'auth/network-request-failed':
          message = 'Error de red. Revisa tu conexión e inténtalo de nuevo';
          break;
      }

      error(message, 'Error de autenticación');
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      user.value = null;
      admin.value = false;
      success('Sesión cerrada correctamente', 'Hasta pronto');
    } catch (err) {
      error('Error al cerrar sesión', 'Error');
    }
  };

  const checkAdminStatus = async (firebaseUser: User | null): Promise<void> => {
    if (!firebaseUser) {
      admin.value = false;
      return;
    }

    try {
      const adminRef = doc(db, 'admins', firebaseUser.uid);
      const adminSnap = await getDoc(adminRef);
      admin.value = adminSnap.exists();
    } catch (err) {
      console.error('Error checking admin permissions:', err);
      admin.value = false;
    }
  };

  const initAuth = () => {
    if (unsubscribe.value) return;
    loading.value = true;

    unsubscribe.value = onAuthStateChanged(auth, async (firebaseUser) => {
      user.value = firebaseUser;
      await checkAdminStatus(firebaseUser);
      loading.value = false;
    });
  };

  const cleanup = () => {
    if (unsubscribe.value) {
      unsubscribe.value();
      unsubscribe.value = null;
    }
  };

  // Inicializar autenticación automáticamente
  onMounted(() => {
    subscribers += 1;
    initAuth();
  });

  onUnmounted(() => {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers === 0) {
      cleanup();
    }
  });

  return {
    user: computed(() => user.value),
    isAuthenticated,
    isAdmin,
    loading: computed(() => loading.value),
    login,
    logout,
    initAuth,
    cleanup,
  };
}
